<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { displayHTMLContent } from '@/utilities/helpers.js'
import { NOTIFICATION_SEVERITY_TYPES } from '@/constants/constants.js'
import { SYSTEM_NOTIFICATION_SEVERITY_CONFIG } from '@/constants/systemNotification.js'
import BaseButton from '@/components/BaseButton.vue'
import ModalContent from '@/components/ModalContent.vue'

const props = defineProps({
  notification: { required: true, type: Object },
  isShowing: { required: false, type: Boolean, default: true },
  isClosable: { required: false, type: Boolean, default: true },
  isMultiple: { required: false, type: Boolean, default: false },
  isFirst: { required: false, type: Boolean, default: true },
  isLast: { required: false, type: Boolean, default: true },
  step: { required: false, type: Number, default: 1 },
  total: { required: false, type: Number, default: 1 }
})

const emit = defineEmits(['close', 'next', 'previous', 'confirm'])

// ModalContent locks the page scroll only when is-showing changes, so it is switched on after mount.
const isVisible = ref(false)
const bodyContent = ref(null)
const hasOverflow = ref(false)

let resizeObserver = null

const severity = computed(
  () =>
    SYSTEM_NOTIFICATION_SEVERITY_CONFIG[props.notification.type] ||
    SYSTEM_NOTIFICATION_SEVERITY_CONFIG[NOTIFICATION_SEVERITY_TYPES.INFO]
)

const message = computed(() => displayHTMLContent(props.notification.message || ''))

const hasImage = computed(() => Boolean(props.notification.image))

const progressWidth = computed(() => `${(props.step / props.total) * 100}%`)

const footerClass = computed(() => [
  'system-notification__footer',
  props.isMultiple ? 'system-notification__footer--multiple' : 'system-notification__footer--single',
  { 'system-notification__footer--shadow': hasOverflow.value }
])

const scrollContainer = () => bodyContent.value?.closest('.modal-body')

const updateOverflow = () => {
  const container = scrollContainer()

  hasOverflow.value = container ? container.scrollHeight > container.clientHeight : false
}

const observeBody = () => {
  resizeObserver?.disconnect()

  const container = scrollContainer()

  if (!container) {
    return
  }

  resizeObserver = new ResizeObserver(updateOverflow)
  resizeObserver.observe(container)
  resizeObserver.observe(bodyContent.value)
}

watch(
  () => props.isShowing,
  isShowing => {
    isVisible.value = isShowing
  }
)

watch(
  () => [isVisible.value, props.notification.id],
  async ([isShowing]) => {
    if (!isShowing) {
      resizeObserver?.disconnect()
      return
    }

    await nextTick()

    const container = scrollContainer()

    if (container) {
      container.scrollTop = 0
    }

    observeBody()
    updateOverflow()
  },
  { immediate: true }
)

onMounted(() => {
  isVisible.value = props.isShowing
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})
</script>

<template>
  <modal-content
    class="system-notification"
    modal-size="system-notification__dialog"
    header-class="system-notification__header"
    modal-body-class="system-notification__body"
    :footer-class="footerClass"
    :is-showing="isVisible"
    :is-closable="isClosable"
    :is-scrollable="true"
    @close="emit('close')"
  >
    <template #header>
      <div class="system-notification__title">
        <i
          class="mdi system-notification__icon"
          :class="[severity.icon, `system-notification-severity--${severity.modifier}`]"
          aria-hidden="true"
        />
        <h5 class="system-notification__title-text">{{ $t(severity.titleKey) }}</h5>
      </div>
    </template>

    <template #body>
      <div ref="bodyContent" class="system-notification__content">
        <a v-if="hasImage === true" :href="notification.image" target="_blank" rel="noopener">
          <img :src="notification.image" class="system-notification__image" alt="" @load="updateOverflow" />
        </a>
        <div v-if="message.type === 'html'" class="system-notification__message" v-html="message.content" />
        <i18n-t v-else :keypath="notification.message" tag="p" class="system-notification__message" />
      </div>
    </template>

    <template #footer>
      <base-button
        v-if="isMultiple === false"
        item-class="btn-primary system-notification__button system-notification__button--block"
        @click="emit('confirm')"
      >
        {{ notification.cta || $t('components.system_notification_modal.ok') }}
      </base-button>

      <template v-else>
        <div class="system-notification__stepper">
          <div
            class="system-notification__progress"
            role="progressbar"
            :aria-valuenow="step"
            aria-valuemin="1"
            :aria-valuemax="total"
          >
            <div class="system-notification__progress-bar" :style="{ width: progressWidth }" />
          </div>
          <span class="system-notification__step">{{ step }}/{{ total }}</span>
        </div>

        <div class="system-notification__actions">
          <base-button
            v-if="isFirst === false"
            item-class="btn-outline-secondary system-notification__button system-notification__button--fixed"
            @click="emit('previous')"
          >
            {{ $t('components.system_notification_modal.previous') }}
          </base-button>
          <base-button
            v-if="isLast === true"
            item-class="btn-secondary system-notification__button system-notification__button--fixed"
            @click="emit('confirm')"
          >
            {{ $t('components.system_notification_modal.close') }}
          </base-button>
          <base-button v-else item-class="btn-primary system-notification__button" @click="emit('next')">
            {{ $t('components.system_notification_modal.continue') }}
          </base-button>
        </div>
      </template>
    </template>
  </modal-content>
</template>

<style scoped>
:deep(.system-notification__dialog) {
  --bs-modal-width: 500px;

  .modal-content {
    min-height: 460px;
    max-height: 720px;
    border: 1px solid var(--border-subtle);
    border-radius: 4px;
    overflow: hidden;
  }

  .modal-header.system-notification__header {
    padding: 24px;
    border-bottom: 0;
    align-items: center;

    > button {
      padding: 0;
    }
  }

  .modal-body.system-notification__body {
    padding: 0 24px 24px;
  }

  .modal-footer.system-notification__footer {
    margin: 0 !important;
    padding: 24px 24px 36px;
    border-top: 0;
    flex-wrap: nowrap;
    justify-content: space-between;
    gap: 12px;
    background-color: var(--white);
    position: relative;

    > * {
      margin: 0;
    }
  }

  .modal-footer.system-notification__footer--single {
    padding-left: 40px;
    padding-right: 40px;
  }

  .modal-footer.system-notification__footer--shadow {
    box-shadow: 0 -8px 4px rgba(0, 0, 0, 0.05);
  }
}

.system-notification__title {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.system-notification__title-text {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--secondary);
}

.system-notification__icon {
  font-size: 20px;
  line-height: 1;
}

.system-notification__content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.system-notification__image {
  display: block;
  width: 100%;
  height: auto;
}

.system-notification__message {
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  color: var(--gray-800);
  word-break: break-word;

  :deep(p) {
    margin-bottom: 24px;
  }

  :deep(p:last-child) {
    margin-bottom: 0;
  }

  :deep(img) {
    max-width: 100%;
  }

  :deep(ul) {
    padding-left: 1em;
  }
}

.system-notification__stepper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.system-notification__progress {
  position: relative;
  width: 96px;
  height: 8px;
  border-radius: 100px;
  background-color: var(--neutral-100);
  overflow: hidden;
}

.system-notification__progress-bar {
  height: 100%;
  border-radius: 100px;
  background-color: var(--primary);
  transition: width 0.2s ease;
}

.system-notification__step {
  font-size: 13px;
  font-weight: 500;
  line-height: 1.12;
  color: var(--secondary-active);
  white-space: nowrap;
}

.system-notification__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.system-notification__button {
  height: 40px;
  padding: 10px 18px;
  border-radius: 6px;
  font-size: 14.222px;
  font-weight: 500;
  line-height: 1.12;
  white-space: nowrap;
}

.system-notification__button--fixed {
  min-width: 101px;
}

.system-notification__button--block {
  width: 100%;
}

@media (max-width: 575.98px) {
  :deep(.system-notification__dialog) {
    .modal-footer.system-notification__footer {
      padding: 20px 16px 24px;
    }
  }

  .system-notification__progress {
    width: 48px;
  }

  .system-notification__actions {
    gap: 8px;
  }

  .system-notification__button--fixed {
    min-width: 0;
  }
}
</style>
